import { ApplicationRef, inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TranslocoService } from '@jsverse/transloco';
import { concat, first, interval } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Acompanha o ciclo de vida do Service Worker: avisa o usuário quando uma nova
 * versão da aplicação está pronta e recarrega quando ele aceita, além de
 * recuperar a aplicação de um cache corrompido.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private swUpdate = inject(SwUpdate);
  private appRef = inject(ApplicationRef);
  private snackBar = inject(MatSnackBar);
  private translocoService = inject(TranslocoService);

  /** Período entre as verificações de nova versão no servidor. */
  private static readonly CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

  init(): void {
    if (!this.swUpdate.isEnabled) return;

    this.watchVersionReady();
    this.watchUnrecoverableState();
    this.schedulePeriodicChecks();
  }

  private watchVersionReady(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.promptReload());
  }

  private promptReload(): void {
    const snack = this.snackBar.open(
      this.translocoService.translate('A new version is available.'),
      this.translocoService.translate('Update'),
      { horizontalPosition: 'end', verticalPosition: 'bottom' }
    );

    snack.onAction().subscribe(() => {
      this.swUpdate.activateUpdate().then(() => document.location.reload());
    });
  }

  // Cache irrecuperável (arquivos da versão em uso removidos do servidor):
  // um reload limpo é a única saída.
  private watchUnrecoverableState(): void {
    this.swUpdate.unrecoverable.subscribe(() => document.location.reload());
  }

  // Só verifica depois que a aplicação estabiliza, para não competir com o
  // carregamento inicial.
  private schedulePeriodicChecks(): void {
    const appIsStable$ = this.appRef.isStable.pipe(first((stable) => stable === true));
    concat(appIsStable$, interval(PwaUpdateService.CHECK_INTERVAL_MS)).subscribe(() => {
      this.swUpdate.checkForUpdate().catch(() => void 0);
    });
  }
}
